import { cn } from '@/lib/utils';
import type { CalendarField } from '@/services/bookingCalendarService';
import { buildDayBlocks, type DayBlock } from '@/components/admin/booking/bookingCalendarBlocks';
import BookingSlotFace from '@/components/admin/booking/BookingSlotFace';
import {
  isSlotOngoing,
  isSlotPast,
  toDateKey,
  toMinutes,
} from '@/components/admin/booking/bookingCalendarUtils';

interface BookingDayGridProps {
  fields: CalendarField[];
  date: Date;
  hours: string[];
  stepMinutes?: number;
  selected: Set<string>;
  now: Date;
  onSlot: (field: CalendarField, block: DayBlock) => void;
}

function covered(blocks: Map<string, DayBlock>, hour: string) {
  return [...blocks.values()].some(
    (block) => block.kind === 'booked' && block.span > 1 && hour > block.start && hour < block.end
  );
}

function DayLegend() {
  return (
    <div className="flex flex-wrap items-center gap-4 pt-4 text-xs text-gray-600">
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full border border-gray-400 bg-gray-200" />
        Selesai
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-[#FC480D]" />
        Berlangsung
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full border border-[#0BCF46] bg-[#ECFDF3]" />
        Paid
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full border border-[#FC480D] bg-[#FFF5EE]" />
        DP
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full border border-[#E7515A] bg-[#FEF2F2]" />
        Unpaid
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full border border-gray-400 bg-gray-300" />
        Tutup
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-gray-200" />
        Sudah lewat
      </span>
    </div>
  );
}

// Fixed height in pixels for each hour row in the day grid
const HOUR_ROW_HEIGHT = 80; // h-20
const CARD_VERTICAL_MARGIN = 6; // 6px space top and bottom

export default function BookingDayGrid({
  fields,
  date,
  hours,
  stepMinutes = 60,
  selected,
  now,
  onSlot,
}: BookingDayGridProps) {
  const dateKey = toDateKey(date);
  const safeStep = stepMinutes > 0 ? stepMinutes : 60;
  const showNow = dateKey === toDateKey(now);
  const timeLabel = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  const startMinutes = hours.length > 0 ? toMinutes(hours[0]) : 6 * 60;
  const endMinutes = hours.length > 0 ? toMinutes(hours[hours.length - 1]) + safeStep : 22 * 60;
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const isWithinRange = currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  const minutesFromStart = currentMinutes - startMinutes;
  const topOffsetPx = (minutesFromStart / safeStep) * HOUR_ROW_HEIGHT;

  const columns = fields.map((field) => ({
    field,
    blocks: buildDayBlocks(field.slots, dateKey, selected, safeStep),
  }));

  return (
    <div className="space-y-3">
      {/* Scrollable grid container */}
      <div className="overflow-x-auto rounded-xl border border-[#E0E6ED] bg-white">
        <div className="min-w-[760px]">
          {/* Header Row: List of Lapangan as columns */}
          <div className="flex border-b border-[#E0E6ED] bg-white">
            <div className="w-16 shrink-0 border-r border-[#E0E6ED] p-3" />
            {columns.map(({ field }) => (
              <div
                key={field.slug}
                className="flex-1 border-r border-[#E0E6ED] p-3 text-left last:border-r-0"
              >
                <p className="text-sm font-semibold text-[#0E1726]">{field.name}</p>
                <p className="text-xs text-gray-500">{field.sport_name || 'Vinyl · Indoor'}</p>
              </div>
            ))}
          </div>

          {/* Grid Area with Hours and Booking Blocks */}
          <div className="relative flex">
            {/* Hours axis on the left */}
            <div className="w-16 shrink-0 border-r border-[#E0E6ED] bg-[#FCFDFD]">
              {hours.map((hour) => (
                <div
                  key={hour}
                  className="flex h-20 items-start justify-end pr-2.5 pt-2 text-xs font-medium text-gray-400"
                >
                  {hour.replace(':', '.')}
                </div>
              ))}
            </div>

            {/* Field Columns */}
            {columns.map(({ field, blocks }) => (
              <div
                key={field.slug}
                className="relative min-w-0 flex-1 border-r border-[#E0E6ED] last:border-r-0"
              >
                {/* Background grid cells for each hour */}
                {hours.map((hour) => {
                  const block = blocks.get(hour);
                  const isCovered = covered(blocks, hour);

                  return (
                    <div
                      key={hour}
                      className={cn(
                        'relative h-20 border-b border-gray-100',
                        !block && !isCovered && 'hover:bg-orange-50/30 transition-colors'
                      )}
                    >
                      {block && block.kind !== 'booked' && !isCovered && (
                        <BookingSlotFace
                          block={block}
                          isPast={isSlotPast(dateKey, block.end, now)}
                          onClick={() => onSlot(field, block)}
                        />
                      )}
                    </div>
                  );
                })}

                {/* Booked blocks rendered absolutely with top and bottom margins */}
                {[...blocks.values()]
                  .filter((block) => block.kind === 'booked')
                  .map((block) => {
                    const isOngoing = isSlotOngoing(dateKey, block.start, block.end, now);
                    const isPast = isSlotPast(dateKey, block.end, now);
                    const exactStartIndex = hours.indexOf(block.start);
                    const blockStartMin = toMinutes(block.start);
                    const blockEndMin = toMinutes(block.end);
                    if (exactStartIndex === -1 && (blockStartMin < startMinutes || blockStartMin >= endMinutes)) {
                      return null;
                    }

                    const topRows =
                      exactStartIndex !== -1
                        ? exactStartIndex
                        : (blockStartMin - startMinutes) / safeStep;
                    const durationRows = Math.max(0.5, (blockEndMin - blockStartMin) / safeStep);

                    const topOffset = topRows * HOUR_ROW_HEIGHT + CARD_VERTICAL_MARGIN;
                    const cardHeight = durationRows * HOUR_ROW_HEIGHT - CARD_VERTICAL_MARGIN * 2;

                    return (
                      <BookingSlotFace
                        key={block.slots[0].public_id}
                        block={block}
                        detailed
                        isOngoing={isOngoing}
                        isPast={isPast}
                        fieldLabel={field.name}
                        style={{
                          top: `${topOffset}px`,
                          height: `${cardHeight}px`,
                        }}
                        onClick={() => onSlot(field, block)}
                      />
                    );
                  })}
              </div>
            ))}

            {/* Current time line indicator */}
            {showNow && isWithinRange && (
              <div
                className="pointer-events-none absolute inset-x-0 z-40"
                style={{ top: `${topOffsetPx}px` }}
              >
                <div className="relative border-t-2 border-[#1F1F1F]">
                  <span className="absolute left-2 -top-3 z-50 rounded-full bg-[#1F1F1F] px-2.5 py-0.5 text-[11px] font-semibold text-white shadow-md">
                    {timeLabel}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Legend below the day table */}
      <DayLegend />
    </div>
  );
}
